import { useCustomerCompany } from '../../api/customerCompanies.ts';
import { useCustomerLabel } from '../../api/directory.ts';
import { useT } from '../../i18n/index.ts';
import { joinIdentityParts } from '../../lib/identityLabel.ts';
import { cx } from '../../ui/cx.ts';

const EMPTY_FALLBACK = '—';

export function CustomerRef({
  orgId,
  accountId,
  className,
  fallback,
  withCompany = true,
}: {
  readonly orgId?: string | null;
  readonly accountId?: string | null;
  readonly className?: string;
  readonly fallback?: string;
  /** When false, the CRM company is left to a parent cell (no second copy). */
  readonly withCompany?: boolean;
}): JSX.Element {
  const t = useT();
  const labels = useCustomerLabel();
  const company = useCustomerCompany({ orgId, accountId });
  const resolvedOrgId = orgId === undefined || orgId === null || orgId.length === 0 ? null : orgId;
  const resolvedAccountId =
    accountId === undefined || accountId === null || accountId.length === 0 ? null : accountId;
  const id = resolvedOrgId ?? resolvedAccountId;
  if (id === null) {
    return <span>{fallback ?? EMPTY_FALLBACK}</span>;
  }
  const identity =
    resolvedOrgId !== null ? labels.identityByOrg(resolvedOrgId) : labels.identityByAccount(resolvedAccountId);
  const directoryCompany = company.status === 'resolved' ? null : identity.company;
  const label =
    withCompany && company.status === 'resolved'
      ? joinIdentityParts([
          company.company ?? t('customer.company.missing'),
          identity.id,
          identity.contact_name,
          identity.email,
        ])
      : joinIdentityParts([identity.id, directoryCompany, identity.contact_name, identity.email]);
  return (
    <span
      className={cx('font-mono', className)}
      title={id}
      data-customer-ref={id}
      data-customer-ref-kind={orgId !== undefined && orgId !== null ? 'org' : 'account'}
    >
      {label}
    </span>
  );
}
